(() => {
  // app/scripts/image-slide.js
  if (customElements.get("slide-image-cus")) return;
  let instanceCount = 0;
  customElements.define("slide-image-cus", class extends HTMLElement {
    connectedCallback() {
      if (this.connected) return; 
      this.connected = true;
      if (!this.initialized) {
        this.initialized = true;
        this.slideImage = this.querySelector(".content-image swiper-component");
        this.slideContent = this.querySelector(".content-text swiper-component");
        this.listBlock = Array.from(this.querySelectorAll(".content-text swiper-component .block__item"));
        this.indexActive = 0;
        this.elapsed = 0;
        this.duration = 5000;
        this.playInterval = null;
        this.hoverPaused = false;
        this.motionQuery = window.matchMedia?.("(prefers-reduced-motion: reduce)");
        this.userPaused = Boolean(this.motionQuery?.matches);
        this.makeControls();
        this.onClick = this.handleClick.bind(this);
        this.onClickCapture = event => {
          const control = event.target.closest(".content-text .slider-controls button");
          this.arrowFocusIntent = control && this.contains(control) && document.activeElement === control ? control : null;
        };
        this.onPointerDown = event => {
          this.rotationPointerIntent = event.button === 0 && event.target.closest(".image-rotation-toggle") === this.rotationButton ? !this.userPaused : null;
        };
        this.onPointerCancel = () => { this.rotationPointerIntent = null; };
        this.onFocus = () => this.pause();
        this.onEnter = () => { this.hoverPaused = true; this.updateTimer(); };
        this.onLeave = () => { this.hoverPaused = false; this.updateTimer(); };
        this.onVisibility = () => this.updateTimer();
        this.onMotion = event => { if (event.matches) this.pause(); };
        this.sliderBindings = new Map();
        this.onSwiperLayout = () => this.scheduleSlideSync();
        this.onResize = () => { this.bindSwiperEvents(); this.scheduleSlideSync(); };
        this.swiperObserver = new MutationObserver(() => {
          if (this.bindSwiperEvents()) this.scheduleSlideSync();
        });
        this.showSlide(0);
      }
      if (this.motionQuery?.matches) this.userPaused = true;
      this.updateRotationLabel();
      this.addEventListener("click", this.onClick);
      this.addEventListener("click", this.onClickCapture, true);
      this.addEventListener("pointerdown", this.onPointerDown);
      this.addEventListener("pointercancel", this.onPointerCancel);
      this.addEventListener("focusin", this.onFocus);
      this.addEventListener("pointerenter", this.onEnter);
      this.addEventListener("pointerleave", this.onLeave);
      document.addEventListener("visibilitychange", this.onVisibility);
      window.addEventListener("resize", this.onResize);
      this.motionQuery?.addEventListener("change", this.onMotion);
      for (const wrapper of [this.slideImage, this.slideContent]) {
        if (wrapper) this.swiperObserver.observe(wrapper, { attributes: true, attributeFilter: ["class"] });
      }
      // The wrapper adds slide-initialized after its real Swiper instance exists.
      if (this.bindSwiperEvents()) this.scheduleSlideSync();
      this.updateTimer();
    }
    disconnectedCallback() {
      this.connected = false;
      this.hoverPaused = false;
      this.rotationPointerIntent = null;
      this.arrowFocusIntent = null;
      clearInterval(this.playInterval);
      this.playInterval = null;
      cancelAnimationFrame(this.controlFrame);
      cancelAnimationFrame(this.resizeFrame);
      this.swiperObserver?.disconnect();
      for (const slider of this.sliderBindings?.values() || []) {
        for (const event of ["resize", "breakpoint", "init"]) slider.off?.(event, this.onSwiperLayout);
      }
      this.sliderBindings?.clear();
      this.removeEventListener("click", this.onClick);
      this.removeEventListener("click", this.onClickCapture, true);
      this.removeEventListener("pointerdown", this.onPointerDown);
      this.removeEventListener("pointercancel", this.onPointerCancel);
      this.removeEventListener("focusin", this.onFocus);
      this.removeEventListener("pointerenter", this.onEnter);
      this.removeEventListener("pointerleave", this.onLeave);
      document.removeEventListener("visibilitychange", this.onVisibility);
      window.removeEventListener("resize", this.onResize);
      this.motionQuery?.removeEventListener("change", this.onMotion);
    }
    bindSwiperEvents() {
      let changed = false;
      for (const wrapper of [this.slideImage, this.slideContent]) {
        const slider = wrapper?.getSlide?.();
        const previous = this.sliderBindings.get(wrapper);
        if (previous === slider) continue;
        if (previous) for (const event of ["resize", "breakpoint", "init"]) previous.off?.(event, this.onSwiperLayout);
        this.sliderBindings.delete(wrapper);
        if (slider?.on && !slider.destroyed) {
          for (const event of ["resize", "breakpoint", "init"]) slider.on(event, this.onSwiperLayout);
          this.sliderBindings.set(wrapper, slider);
          changed = true;
        }
      }
      return changed;
    }
    scheduleSlideSync() {
      if (!this.connected) return;
      cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = requestAnimationFrame(() => {
        this.resizeFrame = requestAnimationFrame(() => {
          this.resizeFrame = null;
          if (!this.connected || !this.isConnected) return;
          // Swiper can reset activeIndex after announcing a breakpoint. Reapply
          // the selected item after layout settles without restarting progress.
          for (const wrapper of [this.slideImage, this.slideContent]) {
            const slider = wrapper?.getSlide?.();
            if (slider && !slider.destroyed && slider.activeIndex !== this.indexActive) wrapper.goTo?.(this.indexActive);
          }
          this.paintSelection();
          this.paintProgress();
        });
      });
    }
    makeControls() {
      if (this.listBlock.length < 2) return;
      const id = `image-carousel-${++instanceCount}`;
      const images = Array.from(this.querySelectorAll(".content-image .block__item"));
      this.listBlock.forEach((item, index) => {
        const text = item.querySelector(".block-title span:not(.icon)");
        if (!text) return;
        const button = document.createElement("button");
        button.type = "button";
        button.className = "image-slide-select";
        button.textContent = text.textContent;
        button.setAttribute("aria-pressed", String(index === 0));
        if (images[index]) {
          images[index].id ||= `${id}-image-${index}`;
          button.setAttribute("aria-controls", images[index].id);
        }
        text.replaceWith(button);
        item.querySelector(".line")?.setAttribute("aria-hidden", "true");
      });
      this.rotationButton = document.createElement("button");
      this.rotationButton.type = "button";
      this.rotationButton.className = "image-rotation-toggle";
      (this.querySelector(".content-text .section-header") || this.slideContent?.parentElement || this).append(this.rotationButton);
      this.updateRotationLabel();
    }
    updateRotationLabel() {
      if (!this.rotationButton) return;
      const strings = window.espAccessibilityStrings || {};
      this.rotationButton.textContent = this.userPaused ? strings.imageRotationResume || "Resume rotation" : strings.imageRotationPause || "Pause rotation";
    }
    pause() {
      this.userPaused = true;
      this.updateRotationLabel();
      this.updateTimer();
    }
    handleClick(event) {
      if (event.target.closest(".image-rotation-toggle") === this.rotationButton) {
        // Pointer focus may already have paused rotation. Honor the action seen
        // at pointerdown; keyboard clicks use the current action after focus.
        this.userPaused = event.detail > 0 && this.rotationPointerIntent != null ? this.rotationPointerIntent : !this.userPaused;
        this.rotationPointerIntent = null;
        this.updateRotationLabel();
        this.updateTimer();
        return;
      }
      this.rotationPointerIntent = null;
      const control = event.target.closest(".content-text .slider-controls button");
      if (control && this.contains(control)) {
        const restoreFocus = this.arrowFocusIntent === control;
        this.arrowFocusIntent = null;
        this.pause();
        cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = null;
        cancelAnimationFrame(this.controlFrame);
        this.controlFrame = requestAnimationFrame(() => {
          if (!this.isConnected) return;
          this.showSlide(this.slideContent?.getSlide?.()?.activeIndex ?? this.indexActive);
          if (restoreFocus) this.restoreArrowFocus(control);
        });
        return;
      }
      // Descriptive links retain their normal navigation and are not slide selectors.
      if (event.target.closest("a[href]")) return;
      const index = this.listBlock.indexOf(event.target.closest(".block__item"));
      if (index >= 0) { this.pause(); this.showSlide(index); }
    }
    restoreArrowFocus(control) {
      if (!control.disabled && control.tabIndex >= 0 && control.getAttribute("aria-disabled") !== "true") return;
      if (document.activeElement !== document.body && document.activeElement !== control) return;
      // Preserve Swiper's disabled endpoint, but retain a useful editing position
      // when that specific user activation caused the focused button to disable.
      const other = Array.from(this.slideContent.querySelectorAll(".slider-controls button")).find(button => button !== control && !button.disabled && button.tabIndex >= 0 && button.getAttribute("aria-disabled") !== "true");
      (other || this.rotationButton || this.querySelector('.image-slide-select[aria-pressed="true"]'))?.focus({ preventScroll: true });
    }
    showSlide(index) {
      if (!this.listBlock.length || !Number.isFinite(Number(index))) return;
      this.indexActive = (Number(index) + this.listBlock.length) % this.listBlock.length;
      this.elapsed = 0;
      this.lastTick = performance.now();
      this.slideImage?.goTo?.(this.indexActive);
      this.slideContent?.goTo?.(this.indexActive);
      this.paintSelection();
      this.paintProgress();
    }
    paintSelection() {
      this.listBlock.forEach((item, index) => {
        item.classList.toggle("swiper-slide-active", index === this.indexActive);
        item.querySelector(".image-slide-select")?.setAttribute("aria-pressed", String(index === this.indexActive));
      });
    }
    paintProgress() {
      this.listBlock.forEach((item, index) => {
        const bar = item.querySelector(".line");
        if (bar) bar.style.width = `${index === this.indexActive ? Math.min(100, this.elapsed / this.duration * 100) : 0}%`;
      });
    }
    updateTimer() {
      const running = this.connected && this.isConnected && this.listBlock.length > 1 && !this.userPaused && !this.hoverPaused && !document.hidden;
      if (!running) {
        if (this.playInterval !== null) {
          this.elapsed += Math.max(0, performance.now() - this.lastTick);
          this.paintProgress();
          clearInterval(this.playInterval);
          this.playInterval = null;
        }
        return;
      }
      if (this.playInterval !== null) return;
      this.lastTick = performance.now();
      // One owned timer advances both the progress indicator and the slide.
      this.playInterval = setInterval(() => {
        const now = performance.now();
        this.elapsed += Math.max(0, now - this.lastTick);
        this.lastTick = now;
        if (this.elapsed >= this.duration) this.showSlide(this.indexActive + 1);
        else this.paintProgress();
      }, 50);
    }
  });
})();
